import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  ListSubheader,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { VariableSizeList as VirtualList } from 'react-window'
import useInnovaTheme from './hooks/useInnovaTheme'
import { appColors } from 'utils'

export function PickList({
  value = [],
  onChange,
  single = false,
  items,
  sections,
  getId = (x) => {
    if (typeof x === 'string') return x
    if (x?.id) return x.id
    if (x?.value) return x.value
    return String(x)
  },
  getLabel = (x) => {
    if (typeof x === 'string') return x
    if (x?.label) return x.label
    return String(x)
  },
  maxHeight = 420,
  rowHeight = 40,
  headerHeight = 34,
}) {
  const { theme, getTextColor, getActionBarColor } = useInnovaTheme()
  const textColor = getTextColor()
  const bg = getActionBarColor()

  const selected = useMemo(() => new Set(Array.isArray(value) ? value : []), [value])

  const normalizedSections = useMemo(() => {
    if (Array.isArray(sections)) return sections
    if (Array.isArray(items)) return [{ header: null, data: items }]
    return []
  }, [sections, items])

  const flat = useMemo(() => {
    const out = []
    normalizedSections.forEach((s, si) => {
      const header = s?.header ?? null
      const data = s?.data ?? []

      if (header) {
        out.push({ type: 'header', key: `h-${si}-${header}`, header })
      }

      data.forEach((item, ii) => {
        const id = getId(item)
        out.push({
          type: 'item',
          key: `i-${si}-${ii}-${String(id)}`,
          item,
          id,
          label: getLabel(item),
        })
      })
    })
    return out
  }, [normalizedSections, getId, getLabel])

  const allItemIds = useMemo(() => {
    return flat.filter((x) => x.type === 'item').map((x) => x.id)
  }, [flat])

  const toggle = useCallback(
    (id) => {
      if (!onChange) return

      if (single) {
        const isSelected = selected.has(id)
        onChange(isSelected ? [] : [id])
        return
      }

      const next = new Set(selected)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      onChange(Array.from(next))
    },
    [onChange, selected, single],
  )

  const height = useMemo(() => {
    const approx = Math.min(maxHeight, 10 * rowHeight + 2 * headerHeight)
    return Math.max(140, approx)
  }, [maxHeight, rowHeight, headerHeight])

  const sizes = useMemo(() => {
    return flat.map((e) => (e.type === 'header' ? headerHeight : rowHeight))
  }, [flat, headerHeight, rowHeight])

  const offsets = useMemo(() => {
    const out = new Array(sizes.length)
    let acc = 0
    for (let i = 0; i < sizes.length; i++) {
      out[i] = acc
      acc += sizes[i]
    }
    return out
  }, [sizes])

  const headerAtOrBeforeIndex = useMemo(() => {
    let current = null
    const out = new Array(flat.length)
    for (let i = 0; i < flat.length; i++) {
      if (flat[i]?.type === 'header') current = flat[i]?.header ?? null
      out[i] = current
    }
    return out
  }, [flat])

  const findIndexAtOffset = useCallback(
    (scrollOffset) => {
      let lo = 0
      let hi = offsets.length - 1
      let ans = 0
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        if (offsets[mid] <= scrollOffset) {
          ans = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }
      return ans
    },
    [offsets],
  )

  const [scrollOffset, setScrollOffset] = useState(0)

  const activeStickyHeader = useMemo(() => {
    if (!flat.length) return null
    const idx = findIndexAtOffset(scrollOffset)
    return headerAtOrBeforeIndex[idx] ?? null
  }, [flat.length, findIndexAtOffset, headerAtOrBeforeIndex, scrollOffset])

  const listRef = useRef(null)

  const getItemSize = useCallback((index) => sizes[index] ?? rowHeight, [sizes, rowHeight])

  const Inner = useMemo(
    () =>
      React.forwardRef(function InnerElement({ style, children, ...rest }, ref) {
        return (
          <div
            ref={ref}
            style={{
              ...style,
              boxSizing: 'border-box',
            }}
            {...rest}>
            {children}
          </div>
        )
      }),
    [],
  )

  const HeaderBar = useCallback(
    ({ header }) => {
      if (!header) return null

      return (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pr: 1.9,
            zIndex: 5,
          }}>
          <Box
            sx={{
              height: headerHeight,
              display: 'flex',
              alignItems: 'center',
              px: 1,
              background:
                theme === 'dark'
                  ? `linear-gradient(180deg, ${bg} 100%, rgba(0,0,0,0.15) 100%)`
                  : `linear-gradient(180deg, ${bg} 100%, rgba(255,255,255,0.35) 100%)`,
              boxShadow: theme === 'dark' ? '0 8px 18px rgba(0,0,0,0.25)' : '0 8px 18px rgba(0,0,0,0.10)',
              borderBottom: '1px solid',
              borderColor: appColors.borderColorLight,
            }}>
            <Typography
              sx={{
                color: textColor,
                fontSize: 13,
                fontWeight: 900,
                lineHeight: 1,
                letterSpacing: 0.2,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
              {header}
            </Typography>
          </Box>
        </Box>
      )
    },
    [bg, headerHeight, textColor, theme],
  )

  const Row = useCallback(
    ({ index, style }) => {
      const entry = flat[index]
      if (!entry) return null

      if (entry.type === 'header') {
        return (
          <Box style={style} sx={{ display: 'flex', alignItems: 'center' }}>
            <ListSubheader
              disableSticky
              sx={{
                width: '100%',
                fontSize: 13,
                fontWeight: 900,
                lineHeight: 1,
                color: textColor,
                px: 1,
                py: 0.75,
                background:
                  theme === 'dark'
                    ? `linear-gradient(180deg, ${bg} 0%, rgba(0,0,0,0.10) 100%)`
                    : `linear-gradient(180deg, ${bg} 0%, rgba(255,255,255,0.25) 100%)`,
                borderBottom: '1px solid',
                borderColor: appColors.borderColorLight,
              }}>
              {entry.header}
            </ListSubheader>
          </Box>
        )
      }

      const checked = selected.has(entry.id)

      return (
        <Box
          style={style}
          onClick={() => toggle(entry.id)}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggle(entry.id)
            }
          }}
          sx={{
            px: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            userSelect: 'none',
            borderBottom: '1px solid',
            borderColor: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)',
            backgroundColor: checked
              ? theme === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)'
              : 'transparent',
            '&:hover': {
              backgroundColor: checked
                ? theme === 'dark'
                  ? 'rgba(255,255,255,0.10)'
                  : 'rgba(0,0,0,0.08)'
                : theme === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
            },
          }}>
          <Checkbox
            checked={checked}
            tabIndex={-1}
            onChange={() => toggle(entry.id)}
            size='small'
            sx={{
              p: 0.5,
              color: textColor,
              '&.Mui-checked': {
                color: appColors.itemTextColor,
              },
            }}
          />
          <Typography
            sx={{
              color: textColor,
              fontSize: 14,
              fontWeight: checked ? 800 : 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}>
            {entry.label}
          </Typography>
        </Box>
      )
    },
    [flat, selected, toggle, theme, textColor, bg],
  )

  const selectedCount = useMemo(() => {
    return allItemIds.reduce((count, id) => count + (selected.has(id) ? 1 : 0), 0)
  }, [allItemIds, selected])

  const allSelected = !single && allItemIds.length > 0 && selectedCount === allItemIds.length
  const partiallySelected = !single && selectedCount > 0 && selectedCount < allItemIds.length

  const toggleAll = useCallback(() => {
    if (!onChange || single) return
    onChange(allSelected ? [] : allItemIds)
  }, [onChange, single, allSelected, allItemIds])

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 1,
        border: '1px solid',
        borderColor: appColors.borderColorLight,
        backgroundColor: bg,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: theme === 'dark' ? 'inset 0 0 0 1px rgba(255,255,255,0.02)' : 'none',
      }}>
      {!single ? (
        <Box
          sx={{
            height: headerHeight + 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            borderBottom: '1px solid',
            borderColor: appColors.borderColorLight,
            background:
              theme === 'dark'
                ? `linear-gradient(180deg, ${bg} 0%, rgba(255,255,255,0.03) 100%)`
                : `linear-gradient(180deg, ${bg} 0%, rgba(0,0,0,0.02) 100%)`,
          }}>
          <Box
            onClick={toggleAll}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleAll()
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
            }}>
            <Checkbox
              checked={allSelected}
              indeterminate={partiallySelected}
              tabIndex={-1}
              onChange={toggleAll}
              size='small'
              sx={{
                p: 0.5,
                color: textColor,
                '&.Mui-checked': {
                  color: appColors.itemTextColor,
                },
                '&.MuiCheckbox-indeterminate': {
                  color: appColors.itemTextColor+'80',
                },
              }}
            />
            <Typography
              sx={{
                color: textColor,
                fontSize: 14,
                fontWeight: 800,
              }}>
              Select All
            </Typography>
          </Box>

          <Typography
            sx={{
              color: textColor,
              fontSize: 12,
              opacity: 0.8,
            }}>
            {selectedCount} / {allItemIds.length}
          </Typography>
        </Box>
      ) : null}

      <HeaderBar header={activeStickyHeader} />

      <VirtualList
        ref={listRef}
        height={height}
        width='100%'
        itemCount={flat.length}
        itemSize={getItemSize}
        overscanCount={8}
        innerElementType={Inner}
        onScroll={({ scrollOffset: so }) => setScrollOffset(so)}>
        {Row}
      </VirtualList>
    </Box>
  )
}

const SimplePickListDialog = (props) => {
  const { title, items, open, setOpen, onApply, initSelItems, singleItemSelect = false, sections } = props

  const { getActionBarColor } = useInnovaTheme()

  const [selectedItems, setSelectedItems] = useState(Array.isArray(initSelItems) ? initSelItems : [])

  useEffect(() => {
    setSelectedItems(Array.isArray(initSelItems) ? initSelItems : [])
  }, [initSelItems])

  const handleClose = () => {
    setOpen(false)
  }

  const handleSave = () => {
    setOpen(false)
    onApply?.(selectedItems)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          width: '700px',
          maxWidth: '95vw',
          backgroundColor: getActionBarColor(),
          overflow: 'hidden',
        },
      }}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getActionBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography
          sx={{
            fontWeight: 600,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {title}
        </Typography>

        <Tooltip title='Close'>
          <IconButton onClick={handleClose} size='small' sx={{ ml: 0.5 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent
        sx={{
          px: 2,
          py: 2,
          backgroundColor: getActionBarColor(),
        }}>
        <PickList
          items={items}
          sections={sections}
          value={selectedItems}
          onChange={setSelectedItems}
          single={singleItemSelect}
          maxHeight={420}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          pb: 2,
          pt: 0.5,
          backgroundColor: getActionBarColor(),
        }}>
        <Button variant='outlined' onClick={handleClose} color='secondary'>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SimplePickListDialog
